import { getUser } from 'src/utils/Common';
import axios from 'src/utils/axios';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import {
  Table, TableCell, TableRow, TableHead, TableBody, TableContainer, Paper,
  Typography, TextField, Container, Breadcrumbs, Link, Grid, Button, CircularProgress,
  TablePagination
} from '@material-ui/core';
import Page from 'src/components/Page';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@material-ui/icons/Search';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { exportToExcel } from 'src/components/ExcelExport';

const useStyles = makeStyles({
  table: {
    minWidth: 650
  },
  header: {
    fontWeight: 'bold',
  },
  stockCell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    width: 40, 
    height: 40, 
    color: '#fff',
    fontWeight: 'bold',
    textAlign: 'center',
    lineHeight: '40px',
    marginLeft: "20px"
  },
  stockGreen: {
    backgroundColor: '#81A263',
  },
  stockRed: {
    backgroundColor: '#C5705D',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
});

function OwnEngineerMaterials() {
  const classes = useStyles();
  const SessionUser = getUser();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const { t } = useTranslation('overview');

  useEffect(() => {
    if (search) {
      const lowercasedSearch = search.toLowerCase();
      const filtered = data.filter((row) =>
        row.MaterialCode.toLowerCase().includes(lowercasedSearch) ||
        row.MaterialDesc.toLowerCase().includes(lowercasedSearch)
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [search, data]);

  const GetOwnEngineerMaterials = () => {
    setLoading(true);
    axios.get(`/GetOwnEngineerMaterials/${SessionUser.ServiceShopId}`)
      .then((response) => {
        setData(response.data);
        setFilteredData(response.data); // Set the filtered data as well
        setLoading(false);
      })
      .catch((error) => {
        setData([]);
        setFilteredData([]);
        setLoading(false);
      });
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleExport = () => {
    const exportData = data.map((row) => ({
      'Material Code': row.MaterialCode || '',
      'Material Description': row.MaterialDesc || '',
      'Available Stock': row.Stock || 0,
      'Blocked Stock': row.BlockedStock || 0
    }));

    exportToExcel(exportData, 'OwnEngineerMaterials');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Page>
      <Container maxWidth="xl" spacing={1}>
        <br />
        <Grid container spacing={1}>
          <Grid item xs={12} sm={3} lg={3}>
            <TextField
              disabled
              size="small"
              fullWidth
              label={t('lblOwnEngineer')}
              id="ServiceShop"
              variant="outlined"
              value={SessionUser.ServiceShopCode + '-' + SessionUser.ServiceShopName}
            />
          </Grid>
          <Grid item xs={12} sm={2} lg={2}>
            <Button
              variant="contained"
              style={{
                backgroundColor: '#81A263',
                color: 'white'
              }}
              fullWidth
              endIcon={<SearchIcon />}
              onClick={GetOwnEngineerMaterials}
            >
              {t('search')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={2} lg={2}>
            <Button
              variant="contained"
              style={{
                backgroundColor: '#81A263',
                color: 'white'
              }}
              fullWidth
              disabled={loading || data.length === 0}
              endIcon={<CloudDownloadIcon />}
              onClick={handleExport}
            >
              {t('lblExport')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={5} lg={5}/>
          <Grid item xs={12} sm={5} lg={5}>
            <TextField
              size="small"
              fullWidth
              label={t('lblSearchMaterial')}
              variant="outlined"
              value={search}
              onChange={handleSearchChange}
            />
          </Grid>
        </Grid>
        <br />
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.header}>{t('lblMaterialCode')}</TableCell>
                  <TableCell className={classes.header} align="left">{t('lblMaterialDesc')}</TableCell>
                  <TableCell className={classes.header} align="left">{t('lblAvailableStock')}</TableCell>
                  <TableCell className={classes.header} align="left">{t('lblBlockedStock')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.MaterialCode}>
                      <TableCell component="th" scope="row">
                        {row.MaterialCode}
                      </TableCell>
                      <TableCell align="left">{row.MaterialDesc}</TableCell>
                      <TableCell align="right">
                        <div className={`${classes.stockCell} ${classes.stockGreen}`}>
                          {row.Stock}
                        </div>
                      </TableCell>
                      <TableCell align="right">
                        <div className={`${classes.stockCell} ${classes.stockRed}`}>
                          {row.BlockedStock}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}
      </Container>
    </Page>
  );
}

export default OwnEngineerMaterials;
